import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from '@/context/UserProfileContext';
import { GamePlayer, GameSetup } from '@/types/golf';
import { api } from '@/utils/api';
import { mediumImpact } from '@/utils/capacitorUtils';
import { supabase } from '@/utils/supabase';
import {
  IonButton,
  IonIcon,
  useIonToast,
  IonFooter,
  IonToolbar,
  IonHeader,
  IonTitle,
  IonButtons,
  IonPage,
  IonContent,
  IonSpinner
} from '@ionic/react';
import {
  addOutline,
  chevronBackOutline
} from 'ionicons/icons';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  closestCenter,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GolfAvatar } from '../ui/GolfAvatar';
import PlayerEditModal from '../game-setup/PlayerEditModal';
import TeeBadge from '../game-setup/TeeBadge';
import PlayerEditor from '../game-setup/PlayerEditor';

// Define the props for our unified component
interface PlayerComponentProps {
  // Common props
  gameId?: string;
  players?: GamePlayer[];
  course?: any;

  // Specific props for game setup mode
  isSetupMode?: boolean;
  initialData?: Partial<GameSetup>;
  onNext?: (data: Partial<GameSetup>) => void;
  onBack?: () => void;
  nav?: HTMLIonNavElement;

  // Specific props for game management mode
  onUpdateSuccess?: () => void;
}

interface PlayerItemProps {
  player: GamePlayer;
  isCurrentUser: boolean;
  onClick: () => void;
  onRemove?: () => void;
}

// Player Item Component
const PlayerItem = ({ player, onClick }: PlayerItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: player.userId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1000 : 1,
    touchAction: 'none' as const,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex flex-col items-center relative ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      onClick={() => {
        if (!isDragging) {
          onClick();
        }
      }}
    >
      {/* Player Avatar */}
      <div className="relative mb-2">
        <GolfAvatar
          name={player.name}
          imageUrl={player.imageUrl}
          className='aspect-square size-full rounded-lg'
        />
      </div>

      {/* Player Name & Handicap */}
      <div className="text-center">
        <div className="font-medium text-sm">{player.name.split(' ')[0]}</div>
        <div className="text-xs text-gray-500">
          HCP {typeof player.handicap === 'string' ? parseInt(String(player.handicap)) : (player.handicap || 54)}
        </div>

        {/* Tee Badge */}
        <div className="mt-1 flex justify-center">
          <TeeBadge teeName={player.teeName} defaultTee={player.teeName} size="sm" />
        </div>
      </div>
    </div>
  );
};

interface EmptySlotProps {
  index: number;
  isOver: boolean;
  onClick: () => void;
}

// Empty Slot Component
const EmptySlot = React.forwardRef<HTMLDivElement, EmptySlotProps>(({ index, isOver, onClick }, ref) => {
  const { t } = useTranslation();

  return (
    <div
      ref={ref}
      className={`flex flex-col items-center justify-center aspect-square rounded-lg border-2 border-dashed ${isOver ? 'border-primary bg-primary/10' : 'border-gray-300'}`}
      onClick={onClick}
      data-droppable="true"
      data-slot-index={index}
    >
      <div className="size-10 rounded-lg bg-gray-100 flex items-center justify-center">
        <IonIcon icon={addOutline} className="size-5 text-gray-400" />
      </div>
      <div className="text-center mt-2">
        <div className="text-xs text-gray-500">{t('add_player')}</div>
      </div>
    </div>
  );
});

/**
 * Unified PlayerComponent that handles both game setup and game management
 * This component replaces both GameSetupPlayers and PlayerManagement
 */
const PlayerComponent: React.FC<PlayerComponentProps> = ({
  gameId,
  players: initialPlayers,
  course,
  isSetupMode = false,
  initialData,
  onNext,
  onBack,
  nav,
  onUpdateSuccess
}) => {
  const { t } = useTranslation();
  const { profile } = useUserProfile();
  const [presentToast] = useIonToast();

  // Common state
  const [players, setPlayers] = useState<GamePlayer[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<GamePlayer | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for drag and drop functionality
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activePlayer, setActivePlayer] = useState<GamePlayer | null>(null);
  const [targetSlot, setTargetSlot] = useState<number | null>(null);
  const slotRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Initialize players based on mode
  useEffect(() => {
    if (isSetupMode && initialData) {
      // Setup mode: Initialize with players from initialData or add current user
      if (initialData.players && initialData.players.length > 0) {
        setPlayers(initialData.players);
      } else if (profile) {
        // Add current user as the first player
        const defaultTee = initialData.course?.tees?.[0];
        setPlayers([{
          userId: profile.userId,
          name: profile.name || profile.username,
          handicap: profile.handicap?.toString() || '0',
          teeName: defaultTee?.teeName,
          teeBoxId: defaultTee?.teeId,
          imageUrl: profile.imageUrl,
          email: profile.email
        }]);
      }
    } else {
      // Management mode: Just use the provided players
      setPlayers(initialPlayers || []);
    }
  }, [initialPlayers, initialData, isSetupMode, profile]);

  // Update player data in the database
  const updatePlayersInGame = async (updatedPlayers: GamePlayer[]) => {
    if (!gameId) return;

    try {
      // Update game with new player data
      await api.put(`/game/${gameId}`, {
        players: updatedPlayers
      });

      // Notify other players about the update
      await supabase
        .from('game_settings_updates')
        .insert({
          game_id: gameId,
          updated_at: new Date().toISOString()
        });

      if (onUpdateSuccess) {
        onUpdateSuccess();
      }

      presentToast({
        message: t('players_updated'),
        duration: 2000,
        color: 'success'
      });

      mediumImpact();
    } catch (error) {
      console.error('Error updating players:', error);
      presentToast({
        message: t('error_updating_players'),
        duration: 2000,
        color: 'danger'
      });
    }
  };

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeId = active.id as string;
    const activePlayerIndex = players.findIndex(p => p.userId === activeId);
    
    if (activePlayerIndex !== -1) {
      setActivePlayer(players[activePlayerIndex]);
      setActiveId(activeId);
    }
  };

  // Handle drag over
  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;

    if (over) {
      // Check if we're over a droppable slot
      const isOverEmptySlot = over.data.current?.droppable === true;
      const slotIndex = over.data.current?.slotIndex;

      if (isOverEmptySlot && slotIndex !== undefined) {
        setTargetSlot(slotIndex);
      } else if (over.id !== activeId) {
        // We're over another player
        const overIndex = players.findIndex(p => p.userId === over.id);
        if (overIndex !== -1) {
          setTargetSlot(overIndex);
        }
      }
    } else {
      setTargetSlot(null);
    }
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveId(null);
    setActivePlayer(null);
    setTargetSlot(null);
    
    if (!over) return;
    
    const activeId = active.id;
    const overId = over.id;
    
    // If dragging over the same item, do nothing
    if (activeId === overId) return;
    
    const activeIndex = players.findIndex(p => p.userId === activeId);
    
    // If we're dragging over an empty slot, handle that
    if (String(overId).includes('slot-')) {
      // No need to do anything special for empty slots in this implementation
      return;
    }
    
    // Otherwise, we're reordering players
    const overIndex = players.findIndex(p => p.userId === overId);
    
    if (activeIndex !== -1 && overIndex !== -1) {
      const newPlayers = arrayMove(players, activeIndex, overIndex);
      setPlayers(newPlayers);
      
      // Update the game data based on mode
      if (isSetupMode && initialData) {
        updateGameData({
          ...initialData,
          players: newPlayers
        });
      } else if (gameId) {
        updatePlayersInGame(newPlayers);
      }
    }
  };

  // Edit a player
  const handleEditPlayer = (player: GamePlayer) => {
    setSelectedPlayer(player);
    setIsEditModalOpen(true);
  };

  // Update a player's info
  const handleUpdatePlayer = (updatedPlayer: GamePlayer) => {
    const updatedPlayers = players.map(player =>
      player.userId === updatedPlayer.userId ? updatedPlayer : player
    );
    setPlayers(updatedPlayers);

    if (gameId) {
      updatePlayersInGame(updatedPlayers);
    }
  };

  // Delete a player
  const handleDeletePlayer = (playerId: string) => {
    const updatedPlayers = players.filter(player => player.userId !== playerId);
    setPlayers(updatedPlayers);

    if (gameId) {
      updatePlayersInGame(updatedPlayers);
    }
  };

  // Check if a player is the current user
  const isCurrentUser = (player: GamePlayer) => {
    return player.userId === profile?.userId;
  };

  // Navigation functions for player management
  const navigateToAddPlayer = () => {
    if (!nav) {
      // If no navigation provided, show a toast
      presentToast({
        message: t('add_player_coming_soon'),
        duration: 2000,
        color: 'primary'
      });
      return;
    }
    
    // Create a wrapper component to avoid React hooks issues with IonNav
    const PlayerEditorWrapper = () => (
      <PlayerEditor
        nav={nav}
        existingPlayerIds={players.map(p => p.userId)}
        onAddPlayer={(player: GamePlayer) => {
          // Ensure player has a default tee assigned if none was selected
          const defaultTee = course?.tees?.[0] || initialData?.course?.tees?.[0];
          const updatedPlayer: GamePlayer = {
            ...player,
            teeBoxId: defaultTee?.teeId || '',
            teeName: defaultTee?.teeName || '',
          };

          const newPlayers = [...players, updatedPlayer];
          setPlayers(newPlayers);

          // Update game data based on mode
          if (isSetupMode && initialData) {
            updateGameData({
              ...initialData,
              players: newPlayers
            });
          } else if (gameId) {
            updatePlayersInGame(newPlayers);
          }
        }}
      />
    );

    nav.push(PlayerEditorWrapper);
  };

  // Handle next action in setup mode
  const handleNext = async () => {
    if (onNext && isSetupMode && initialData) {
      try {
        setIsSubmitting(true);
        mediumImpact();

        const submitData = {
          ...initialData,
          players,
          status: 'pending'
        };

        // If we don't have a gameId yet, create the game
        if (!initialData.gameId) {
          const response = await api.post('/game', submitData);
          const result = response.data;

          // Pass the data back to the parent component with the game ID
          const gameData = {
            ...initialData,
            players,
            gameId: result.gameId
          };

          // Pass the complete data including the gameId to the parent component
          onNext(gameData);
        } else {
          // Just pass the current data with players
          onNext({
            ...initialData,
            players
          });
        }
      } catch (error) {
        console.error('Error creating game:', error);
        presentToast({
          message: t('failed_to_create_game'),
          duration: 2000,
          color: 'danger'
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Update game data in the API (for setup mode)
  const updateGameData = async (gameData: Partial<GameSetup>) => {
    try {
      // If we have a gameId, update the existing game
      if (initialData?.gameId) {
        await api.put(`/game/${initialData.gameId}`, {
          ...gameData,
          status: 'pending'
        });
      }

      // Also update initialData to ensure it's preserved when navigating back
      if (initialData && initialData.players !== gameData.players) {
        initialData.players = gameData.players || [];
      }
    } catch (error) {
      console.error('Error updating game data:', error);
    }
  };

  // Function to render the component content
  const renderContent = () => {
    return (
      <>
        <div className='space-y-4'>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">{t('players')}</h2>
          </div>

          <div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              autoScroll={true}
            >
              <SortableContext items={players.map(p => p.userId)} strategy={rectSortingStrategy}>
                <div className="w-full bg-card rounded-lg p-4">
                  <div className="grid grid-cols-4 gap-3">
                    {players.map((player, index) => (
                      <PlayerItem
                        key={player.userId}
                        player={player}
                        isCurrentUser={isCurrentUser(player)}
                        onClick={() => handleEditPlayer(player)}
                        onRemove={() => {
                          if (!isCurrentUser(player)) {
                            handleDeletePlayer(player.userId);
                          }
                        }}
                      />
                    ))}

                    {/* Empty Slots / Add Player Buttons - Droppable */}
                    {Array.from({ length: Math.max(0, 4 - players.length) }).map((_, i) => {
                      const slotIndex = players.length + i;
                      return (
                        <EmptySlot
                          key={`slot-${i}`}
                          index={slotIndex}
                          isOver={targetSlot === slotIndex}
                          onClick={navigateToAddPlayer}
                          ref={(el: HTMLDivElement | null) => slotRefs.current[i] = el}
                        />
                      );
                    })}
                  </div>
                </div>
              </SortableContext>

              {/* Drag Overlay - Shows what's being dragged */}
              <DragOverlay
                adjustScale={true}
                dropAnimation={{
                  sideEffects: defaultDropAnimationSideEffects({
                    styles: {
                      active: {
                        opacity: '0.75',
                      },
                    },
                  }),
                }}
              >
                {activePlayer && (
                  <div className="flex flex-col items-center opacity-90">
                    <div className="relative mb-2">
                      <GolfAvatar
                        name={activePlayer.name}
                        imageUrl={activePlayer.imageUrl}
                        className='aspect-square size-full rounded-lg'
                      />
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-sm">{activePlayer.name.split(' ')[0]}</div>
                    </div>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </div>
          
          {/* WIP Add Group Button - Disabled for now as per requirements */}
          <button
            disabled
            className="w-full items-center gap-2 bg-card rounded-xl p-4 opacity-70 hidden"
          >
            <div className="size-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <IonIcon icon={addOutline} className="size-5 text-gray-400" />
            </div>
            <span className="font-bold">{t('add_group')}</span>
          </button>
        </div>

        {/* Player Edit Modal */}
        <PlayerEditModal
          isOpen={isEditModalOpen}
          player={selectedPlayer!}
          gameSetup={initialData}
          course={course || initialData?.course}
          isCurrentUser={selectedPlayer ? isCurrentUser(selectedPlayer) : false}
          onClose={() => setIsEditModalOpen(false)}
          onDeletePlayer={() => {
            if (selectedPlayer && !isCurrentUser(selectedPlayer)) {
              handleDeletePlayer(selectedPlayer.userId);
            }
            setIsEditModalOpen(false);
            setSelectedPlayer(null);
          }}
          onUpdatePlayer={(updatedPlayer) => {
            handleUpdatePlayer(updatedPlayer);
            setIsEditModalOpen(false);
          }}
          onSave={(updatedPlayer) => {
            handleUpdatePlayer(updatedPlayer);
            setIsEditModalOpen(false);
          }}
        />
      </>
    );
  };

  // Render as full page layout in setup mode
  if (isSetupMode && nav) {
    return (
      <IonPage>
        <IonHeader className="ion-no-border">
          <IonToolbar color="light">
            <IonButtons slot="start">
              <IonButton onClick={onBack}>
                <IonIcon icon={chevronBackOutline} slot="icon-only" />
              </IonButton>
            </IonButtons>
            <IonTitle>{t('add_players')}</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent color="light" className="ion-padding">
          {renderContent()}
        </IonContent>

        <IonFooter className="ion-no-border">
          <IonToolbar color="light">
            <IonButton
              expand="block"
              shape='round'
              onClick={handleNext}
              disabled={players.length === 0 || isSubmitting}
              className="font-bold"
            >
              {isSubmitting ? (
                <>
                  <IonSpinner name="dots" className="mr-2" />
                  {t('creating_game')}
                </>
              ) : (
                t('start')
              )}
            </IonButton>
          </IonToolbar>
        </IonFooter>
      </IonPage>
    );
  }

  // Render as a component in management mode
  return (
    <div className="player-management">
      {renderContent()}
    </div>
  );
};

export default PlayerComponent;
